import {announce} from '@github-ui/aria-live'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {useEffect, useRef} from 'react'

import styles from './ModelPicker.module.css'

export type AutoModelTier = 'efficiency' | 'balance' | 'intelligence'
export type AutoModelTierState = AutoModelTier | 'fast'

export interface AutoTierPickerConfig {
  effectiveTier?: string
  selectedTier?: AutoModelTier
  pendingTier?: AutoModelTierState
  error?: string
  disabled?: boolean
  onTierSelected: (tier: AutoModelTier) => void
}

export type VisibleAutoTierPickerConfig = AutoTierPickerConfig

export const AUTO_MODEL_TIERS = ['efficiency', 'balance', 'intelligence'] as const satisfies readonly AutoModelTier[]

const AUTO_TIER_DETAILS: Record<AutoModelTier, {label: string; description: string}> = {
  efficiency: {
    label: 'Efficiency',
    description: 'Prioritizes speed and efficiency',
  },
  balance: {
    label: 'Balance',
    description: 'Balances speed and intelligence',
  },
  intelligence: {
    label: 'Intelligence',
    description: 'Prioritizes intelligence',
  },
}

export function isVisibleAutoTierPickerConfig(
  config: AutoTierPickerConfig | undefined,
): config is VisibleAutoTierPickerConfig {
  return config !== undefined && config.effectiveTier !== 'fast' && config.pendingTier !== 'fast'
}

export function isAutoModelTier(tier: unknown): tier is AutoModelTier {
  return typeof tier === 'string' && AUTO_MODEL_TIERS.includes(tier as AutoModelTier)
}

export function getAutoTierLabel(tier: AutoModelTier) {
  return AUTO_TIER_DETAILS[tier].label
}

export function getAutoTierModelDisplayNameForTier(modelDisplayName: string, tier: AutoModelTier) {
  return `${modelDisplayName} · ${getAutoTierLabel(tier)}`
}

function getDisplayedAutoTier(config: VisibleAutoTierPickerConfig) {
  if (isAutoModelTier(config.pendingTier)) return config.pendingTier
  if (config.selectedTier) return config.selectedTier
  return isAutoModelTier(config.effectiveTier) ? config.effectiveTier : undefined
}

export function getAutoTierModelDisplayName(modelDisplayName: string, config: VisibleAutoTierPickerConfig) {
  const displayedTier = getDisplayedAutoTier(config)
  return displayedTier ? getAutoTierModelDisplayNameForTier(modelDisplayName, displayedTier) : modelDisplayName
}

export function getAutoTierModelAccessibleLabel(modelDisplayName: string, config: VisibleAutoTierPickerConfig) {
  const displayName = getAutoTierModelDisplayName(modelDisplayName, config)
  const pendingLabel = isAutoModelTier(config.pendingTier) ? getAutoTierLabel(config.pendingTier) : undefined
  const selectedLabel =
    config.selectedTier && config.selectedTier !== config.effectiveTier
      ? getAutoTierLabel(config.selectedTier)
      : undefined

  if (pendingLabel) return `Model: ${displayName}, applying optimization`
  if (selectedLabel) return `Model: ${displayName}, selected for the next message`
  return `Model: ${displayName}`
}

export function AutoTierAnnouncements({config}: {config: VisibleAutoTierPickerConfig}) {
  const pendingTier = isAutoModelTier(config.pendingTier) ? config.pendingTier : undefined
  const pendingLabel = pendingTier ? getAutoTierLabel(pendingTier) : undefined
  const previousPendingTierRef = useRef<AutoModelTier | undefined>(undefined)

  useEffect(() => {
    const committedTier =
      previousPendingTierRef.current === config.effectiveTier ? previousPendingTierRef.current : undefined
    const message =
      config.error ??
      (pendingLabel
        ? `Applying ${pendingLabel}…`
        : committedTier
          ? `${getAutoTierLabel(committedTier)} optimization applied.`
          : undefined)
    previousPendingTierRef.current = pendingTier
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- controlled async status changes must be announced when their props update
    if (message) announce(message)
  }, [config.effectiveTier, config.error, pendingLabel, pendingTier])

  return null
}

interface AutoTierMenuProps {
  config: VisibleAutoTierPickerConfig
  onBack: () => void
}

export function AutoTierMenu({config, onBack}: AutoTierMenuProps) {
  const effectiveTier = isAutoModelTier(config.effectiveTier) ? config.effectiveTier : undefined
  const pendingTier = isAutoModelTier(config.pendingTier) ? config.pendingTier : undefined
  const pendingLabel = pendingTier ? getAutoTierLabel(pendingTier) : undefined
  const selectedTier = pendingTier ?? config.selectedTier ?? effectiveTier
  const selectedLabel =
    !pendingTier && config.selectedTier && config.selectedTier !== effectiveTier
      ? getAutoTierLabel(config.selectedTier)
      : undefined

  return (
    <>
      <ActionList>
        <ActionList.Item
          onSelect={event => {
            event.preventDefault()
            onBack()
          }}
        >
          <ActionList.LeadingVisual>
            <ArrowLeftIcon />
          </ActionList.LeadingVisual>
          Back to models
        </ActionList.Item>
        <ActionList.Divider />
        <ActionList.Group selectionVariant="single">
          <ActionList.GroupHeading>Optimized for</ActionList.GroupHeading>
          {AUTO_MODEL_TIERS.map(tier => {
            const details = AUTO_TIER_DETAILS[tier]
            return (
              <ActionList.Item
                key={tier}
                selected={tier === selectedTier}
                disabled={config.disabled || pendingLabel !== undefined}
                onSelect={() => config.onTierSelected(tier)}
              >
                {details.label}
                <ActionList.Description variant="block">{details.description}</ActionList.Description>
              </ActionList.Item>
            )
          })}
        </ActionList.Group>
      </ActionList>
      {pendingLabel || selectedLabel || config.error ? (
        <div className={styles.autoTierMessages}>
          {pendingLabel ? <div className={styles.autoTierStatus}>Applying {pendingLabel}…</div> : null}
          {selectedLabel ? (
            <div className={styles.autoTierStatus}>{selectedLabel} will be used for the next message.</div>
          ) : null}
          {config.error ? <div className={styles.autoTierError}>{config.error}</div> : null}
        </div>
      ) : null}
    </>
  )
}
